import { useEffect, useRef, useState } from 'react';
import type { Answer } from '../domain/friendship-card-v1';
import { api } from './api';
import type { CountrySearchResult, LocationSearchResult } from './types';
import { AppIcon } from './ParticipantShell';
import { FriendshipSheet } from './FriendshipUI';

// Catalog labels only, kept in memory. No account location lookup or private draft is cached here.
const catalogLabels = new Map<string, string>();
export function CardCityPicker({ answer, onChange, filter = false }: {
  filter?: boolean; answer: Answer; onChange: (patch: Partial<Answer>) => void;
}) {
  const [open, setOpen] = useState(false), [country, setCountry] = useState<CountrySearchResult | null>(null);
  const [countryQuery, setCountryQuery] = useState(''), [cityQuery, setCityQuery] = useState('');
  const [countries, setCountries] = useState<CountrySearchResult[]>([]), [cities, setCities] = useState<LocationSearchResult[]>([]);
  const [loading, setLoading] = useState(false), [error, setError] = useState(''), [retry, setRetry] = useState(0);
  const search = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    search.current?.focus({ preventScroll: true });
  }, [open, country?.countryRegionCode]);
  useEffect(() => {
    let active = true;
    setError(''); setCountries([]); setCities([]);
    if (!open || (country && !cityQuery.trim())) { setLoading(false); return; }
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        if (country) {
          const result = await api<{ results: LocationSearchResult[] }>(`/api/v1/locations/search?locale=zh-CN&countryRegionCode=${country.countryRegionCode}&q=${encodeURIComponent(cityQuery)}`);
          if (active) setCities(result.results);
        } else {
          const result = await api<{ results: CountrySearchResult[] }>(`/api/v1/locations/countries?locale=zh-CN&q=${encodeURIComponent(countryQuery)}`);
          if (active) setCountries(result.results);
        }
      } catch {
        if (active) setError('城市目录暂时无法读取，请重试。');
      } finally { if (active) setLoading(false); }
    }, 180);
    return () => { active = false; clearTimeout(timer); };
  }, [open, country?.countryRegionCode, cityQuery, countryQuery, retry]);
  const summary = answer.cityId ? catalogLabels.get(answer.cityId) ?? '已选城市' : '选择城市';
  const close = () => setOpen(false);
  const choose = (city: LocationSearchResult) => {
    catalogLabels.set(city.placeId, `${city.displayName} · ${city.countryDisplayName || country?.displayName || city.countryRegionCode}`);
    onChange({ cityId: city.placeId, wholeCity: false }); close();
  };
  return <div className="friendship-city">
    <button type="button" className={`friendship-city-summary${answer.cityId ? '' : ' is-empty'}`} aria-haspopup="dialog" aria-expanded={open}
      aria-label={answer.cityId ? `更改城市，${summary}` : '选择城市'} onClick={() => { setCountry(null); setCountryQuery(''); setCityQuery(''); setOpen(true); }}>
      <span>{summary}</span><AppIcon name="chevron" />
    </button>
    {!filter && <><label className="checkbox-line"><input type="checkbox" checked={answer.wholeCity ?? false} disabled={!answer.cityId} onChange={e => onChange({ wholeCity: e.target.checked })} />整个城市都可以</label>
      <label className="checkbox-line"><input type="checkbox" checked={answer.online ?? false} onChange={e => onChange({ online: e.target.checked })} />线上也可以</label></>}
    <FriendshipSheet open={open} onClose={close} title={country ? country.displayName : '选择城市'} focusSearch className="friendship-city-sheet"
      headerAction={country ? <button type="button" className="r4-icon" aria-label="返回国家或地区" onClick={() => { setCountry(null); setCityQuery(''); }}><AppIcon name="back" /></button> : undefined}
      footer={answer.cityId ? <button type="button" className="text-button" onClick={() => { onChange({ cityId: null, wholeCity: false }); close(); }}>清除城市</button> : undefined}>
      <label className="friendship-city-search"><span>{country ? '城市' : '国家或地区'}</span><input key={country?.countryRegionCode ?? 'countries'} ref={search} type="search" autoComplete="off"
        value={country ? cityQuery : countryQuery} onChange={e => country ? setCityQuery(e.target.value) : setCountryQuery(e.target.value)} /></label>
      {loading && <p role="status" className="friendship-picker-status">Loading</p>}
      {error && <div role="alert"><p>{error}</p><button type="button" className="text-button" onClick={() => setRetry(n => n + 1)}>Try again</button></div>}
      <ul className="friendship-picker-results" aria-label={country ? '城市搜索结果' : '国家或地区搜索结果'}>
        {country ? cities.map(city => <li key={city.placeId}><button type="button" onClick={() => choose(city)}><span className="friendship-monogram" aria-hidden="true"><AppIcon name="location" /></span><span className="friendship-picker-copy"><span>{city.displayName}</span>{city.admin1DisplayName && <small>{city.admin1DisplayName}</small>}</span></button></li>)
          : countries.map(item => <li key={item.countryRegionCode}><button type="button" onClick={() => { setCountry(item); setCityQuery(''); }}><span className="friendship-monogram" aria-hidden="true"><AppIcon name="globe" /></span><span className="friendship-picker-copy">{item.displayName}</span><AppIcon name="chevron" /></button></li>)}
      </ul>
      {!loading && !error && (country ? Boolean(cityQuery.trim()) && cities.length === 0 : countries.length === 0) && <p className="friendship-picker-status" role="status">没有符合条件的结果</p>}
    </FriendshipSheet>
  </div>;
}
